/**
 * The words for what the replenishment pull says of a cell when the handover from «Asignación inicial» is why it sent nothing —
 * `covered_by_initial_allocation` (an approved line covers the article at the store) and `handover_ledger_unreadable` (the run refused
 * its DC pulls because the ledger of those pairs could not be read). They are the PLATFORM's, from the group `pullReason` of
 * `GET /launch/initial/reason-labels` (platform ADR 6220): this build carries no words of its own for them.
 *
 * The in-season words are pure functions called from many places (the page, the cells file, the summaries), so the labels are held
 * here, once: a screen that draws a cause calls {@link usePullReasonLabels}, which keeps them current and re-draws it when they come;
 * a file built on a click awaits {@link primePullReasonLabels} first. Until the labels have settled the words are empty — a page
 * draws nothing for a cause rather than its code — and where the read failed, or the platform labels no such cause, one sentence says so
 * (`uncovered-cause.ts`): never the code.
 */

import { NO_REASON_LABELS, readReasonLabels, useReasonLabels, type ReasonLabels } from '@/api';
import { launchReadReasonLabels } from '@/api/generated';

/** The platform's group of the pull's handover causes. */
export const PULL_REASON_GROUP = 'pullReason';

/** The causes of this group, in the run's own order (`./uncovered-cause.ts` places them). */
export const PULL_REASON_CODES = ['handover_ledger_unreadable', 'covered_by_initial_allocation'] as const;

let held: ReasonLabels = NO_REASON_LABELS;

/** Holds the labels the words read. The hook and the file's read call it; a test holds a vocabulary of its own. */
export function holdPullReasonLabels(labels: ReasonLabels): void {
  held = labels;
}

/** Whether the read of the labels failed: no cause has words, and the page says so in one sentence. */
export function pullReasonsUnreadable(): boolean {
  return held.failed;
}

/** The platform's words for a pull cause: its label; `''` while the labels are on their way; `null` where it has none. Pure but for the labels held. */
export function pullReasonWords(code: string): string | null {
  const label = held.labelOrNull(PULL_REASON_GROUP, code);
  if (label !== null) return label;
  return held.loaded ? null : '';
}

/** Keeps the pull causes' labels current for the words below, and re-draws the caller when they arrive. */
export function usePullReasonLabels(): void {
  holdPullReasonLabels(useReasonLabels(launchReadReasonLabels));
}

/** Reads the labels for a file built on a click; never rejects (a failure leaves the code where no word came). */
export async function primePullReasonLabels(language?: string): Promise<void> {
  holdPullReasonLabels(await readReasonLabels(launchReadReasonLabels, language));
}
